/**
 * Ant Design Vue Theme Configuration
 *
 * Integrates Hyperke design tokens with Ant Design Vue's theme system.
 * Ant Design Vue uses a ConfigProvider component that accepts theme tokens.
 *
 * This maps our design tokens to Ant Design's theme token structure.
 */

import type { ThemeConfig } from 'ant-design-vue/es/config-provider/context';

/**
 * Light theme configuration for Ant Design Vue
 * Maps our design tokens to Ant Design's token structure
 */
export const antDesignLightTheme: ThemeConfig = {
  token: {
    // === Colors ===
    // Primary brand color - matches our --color-primary-500
    colorPrimary: '#1890ff',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    colorInfo: '#1890ff',

    // === Functional colors ===
    colorLink: '#1890ff',
    colorLinkHover: '#40a9ff',

    // === Text colors ===
    // These match our WCAG AA compliant text tokens
    colorTextBase: 'rgba(0, 0, 0, 0.88)',      // Primary text
    colorTextSecondary: 'rgba(0, 0, 0, 0.65)',  // Secondary text
    colorTextTertiary: 'rgba(0, 0, 0, 0.45)',   // Tertiary text
    colorTextQuaternary: 'rgba(0, 0, 0, 0.25)', // Disabled text

    // === Background colors ===
    colorBgContainer: '#ffffff',      // Card/container background
    colorBgElevated: '#ffffff',        // Elevated surface
    colorBgLayout: '#f5f5f5',          // Layout background
    colorBgSpotlight: '#262626',       // antd Tooltip background — MUST stay dark; a cream value here once made every tooltip ~1:1 contrast. (The new-leads highlight is --color-bg-spotlight in tokens/index.css, a separate mechanism.)

    // === Border colors ===
    colorBorder: '#d9d9d9',            // Default border
    colorBorderSecondary: '#f0f0f0',  // Subtle border

    // === Placeholder ===
    colorPlaceholder: 'rgba(0, 0, 0, 0.25)',

    // === Typography ===
    fontSize: 14,                       // Base font size
    fontSizeHeading1: 38,               // h1
    fontSizeHeading2: 30,               // h2
    fontSizeHeading3: 24,               // h3
    fontSizeHeading4: 20,               // h4
    fontSizeHeading5: 16,               // h5
    fontSizeLG: 16,                     // Large text
    fontSizeXL: 20,                    // Extra large
    fontSizeSM: 12,                     // Small text

    lineHeight: 1.5,                    // Base line height
    lineHeightHeading1: 1.35,
    lineHeightHeading2: 1.35,
    lineHeightHeading3: 1.35,
    lineHeightHeading4: 1.4,
    lineHeightHeading5: 1.5,

    // === Font weights ===
    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightStrong: 600,  // Not bold (700) to match design tokens

    // === Border radius ===
    borderRadius: 8,                    // Matches our --radius-md
    borderRadiusLG: 12,                 // Matches our --radius-lg
    borderRadiusSM: 4,                  // Matches our --radius-sm

    // === Spacing ===
    marginXS: 8,                        // Matches our --spacing-2
    marginSM: 12,                      // Matches our --spacing-3
    margin: 16,                        // Matches our --spacing-4
    marginMD: 20,                      // Matches our --spacing-5
    marginLG: 24,                      // Matches our --spacing-6
    marginXL: 32,                      // Matches our --spacing-8

    paddingXS: 8,
    paddingSM: 12,
    padding: 16,
    paddingMD: 20,
    paddingLG: 24,
    paddingXL: 32,

    // === Shadows ===
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px rgba(0, 0, 0, 0.02)',
    boxShadowCard: '0 2px 8px rgba(0, 0, 0, 0.08)',
    boxShadowPopover: '0 4px 16px rgba(0, 0, 0, 0.12)',

    // === Motion ===
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',

    motionEaseInOut: 'cubic-bezier(0.4, 0, 0.6, 1)',
    motionEaseOut: 'cubic-bezier(0, 0, 0.2, 1)',
    motionEaseIn: 'cubic-bezier(0.4, 0, 1, 1)',
  },

  components: {
    // === Component-specific tokens ===

    // Button
    Button: {
      borderRadius: 4,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
      paddingInline: 16,
      paddingInlineSM: 12,
      paddingInlineLG: 20,
    },

    // Input
    Input: {
      borderRadius: 4,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
      paddingInline: 12,
    },

    // Select
    Select: {
      borderRadius: 4,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
    },

    // Card
    Card: {
      borderRadius: 12,
      paddingLG: 24,
    },

    // Table
    Table: {
      borderRadius: 8,
      padding: 16,
      paddingXS: 8,
      paddingSM: 12,
    },

    // Modal
    Modal: {
      borderRadius: 12,
      paddingLG: 24,
    },

    // Drawer
    Drawer: {
      paddingLG: 24,
    },

    // Tag
    Tag: {
      borderRadius: 4,
    },

    // Badge
    Badge: {
      borderRadius: 9999,
    },

    // Dropdown
    Dropdown: {
      borderRadius: 8,
      padding: 8,
    },

    // Tooltip
    Tooltip: {
      borderRadius: 6,
    },

    // Popover
    Popover: {
      borderRadius: 8,
    },

    // Form
    Form: {
      itemMarginBottom: 16,
      verticalLabelMargin: 8,
    },

    // Divider
    Divider: {
      marginLG: 24,
      marginSM: 12,
    },

    // Space
    Space: {
      blockMarginXS: 8,
      blockMarginSM: 12,
      blockMargin: 16,
      blockMarginLG: 24,
      blockMarginXL: 32,
    },
  },
};

/**
 * Dark theme configuration for Ant Design Vue
 * Matches our dark mode CSS variables
 */
export const antDesignDarkTheme: ThemeConfig = {
  token: {
    // === Colors ===
    colorPrimary: '#40a9ff',           // Lighter for dark mode
    colorSuccess: '#73d13d',
    colorWarning: '#ffc53d',
    colorError: '#ff7875',
    colorInfo: '#40a9ff',

    // === Functional colors ===
    colorLink: '#40a9ff',
    colorLinkHover: '#69c0ff',

    // === Text colors ===
    // WCAG AA compliant dark mode colors
    colorTextBase: 'rgba(255, 255, 255, 0.88)',
    colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
    colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
    colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',

    // === Background colors ===
    colorBgContainer: '#262626',        // Card background
    colorBgElevated: '#2f2f2f',         // Elevated surface
    colorBgLayout: '#141414',           // Layout background
    colorBgSpotlight: '#1f1f1f',       // antd Tooltip background (dark theme)

    // === Border colors ===
    colorBorder: '#424242',
    colorBorderSecondary: '#303030',

    // === Placeholder ===
    colorPlaceholder: 'rgba(255, 255, 255, 0.25)',

    // === Shadows (lighter for dark mode) ===
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 6px -1px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)',
    boxShadowCard: '0 2px 8px rgba(0, 0, 0, 0.25)',
    boxShadowPopover: '0 4px 16px rgba(0, 0, 0, 0.35)',
  },

  components: {
    // Dark mode component adjustments
    Table: {
      colorBgContainer: '#1f1f1f',
      headerColor: 'rgba(255, 255, 255, 0.88)',
    },
  },

  algorithm: true, // Enable Ant Design's dark mode algorithm
};

/**
 * Get theme configuration based on theme mode
 */
export const getAntDesignTheme = (theme: 'light' | 'dark' = 'light'): ThemeConfig => {
  return theme === 'dark' ? antDesignDarkTheme : antDesignLightTheme;
};

/**
 * Theme configuration export for ConfigProvider
 */
export const themeConfig = {
  light: antDesignLightTheme,
  dark: antDesignDarkTheme,
};

export default getAntDesignTheme;
